import React from 'react';
import { Modal, Button, Typography, Space, Divider } from 'antd';
import { CheckCircle2, ExternalLink, FlaskConical } from 'lucide-react';
import { useSocialContext } from '../context/social';
import { SocialProvider } from '../types/social';
import { tokens } from '../theme/tokens';
import { FEATURE_FLAGS } from '../config/featureFlags';

const { Title, Text } = Typography;

interface ConnectModalProps {
  open: boolean;
  onClose: () => void;
  zIndex?: number;
}

interface ProviderConfig {
  key: SocialProvider;
  label: string;
  description: string;
  color: string;
  icon: React.ReactNode;
}

// SVG icons for TikTok and Instagram (inline, no external dependency)
const TikTokIcon = () => (
  <svg width='22' height='22' viewBox='0 0 24 24' fill='currentColor'>
    <path d='M19.59 6.69a4.83 4.83 0 0 1-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 0 1-2.88 2.5 2.89 2.89 0 0 1-2.89-2.89 2.89 2.89 0 0 1 2.89-2.89c.28 0 .54.04.79.1V9.01a6.27 6.27 0 0 0-.79-.05 6.34 6.34 0 0 0-6.34 6.34 6.34 6.34 0 0 0 6.34 6.34 6.34 6.34 0 0 0 6.33-6.34V8.87a8.18 8.18 0 0 0 4.78 1.52V6.93a4.85 4.85 0 0 1-1.01-.24z' />
  </svg>
);

const InstagramIcon = () => (
  <svg width='22' height='22' viewBox='0 0 24 24' fill='currentColor'>
    <path d='M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zm0-2.163c-3.259 0-3.667.014-4.947.072-4.358.2-6.78 2.618-6.98 6.98-.059 1.281-.073 1.689-.073 4.948 0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98 1.281.058 1.689.072 4.948.072 3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98-1.281-.059-1.69-.073-4.949-.073zm0 5.838c-3.403 0-6.162 2.759-6.162 6.162s2.759 6.163 6.162 6.163 6.162-2.759 6.162-6.163c0-3.403-2.759-6.162-6.162-6.162zm0 10.162c-2.209 0-4-1.79-4-4 0-2.209 1.791-4 4-4s4 1.791 4 4c0 2.21-1.791 4-4 4zm6.406-11.845c-.796 0-1.441.645-1.441 1.44s.645 1.44 1.441 1.44c.795 0 1.439-.645 1.439-1.44s-.644-1.44-1.439-1.44z' />
  </svg>
);

const GoogleIcon = () => (
  <svg width='22' height='22' viewBox='0 0 24 24'>
    <path fill='#4285F4' d='M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z' />
    <path fill='#34A853' d='M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z' />
    <path fill='#FBBC05' d='M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z' />
    <path fill='#EA4335' d='M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z' />
  </svg>
);

const PROVIDERS: ProviderConfig[] = [
  {
    key: 'tiktok',
    label: 'TikTok',
    description: 'Compartí clips del catálogo y ganá puntos por cada view en TikTok.',
    color: '#010101',
    icon: <TikTokIcon />
  },
  {
    key: 'instagram',
    label: 'Instagram Reels',
    description: 'Publicá Reels con contenido del catálogo y acumulá puntos por engagement.',
    color: '#E1306C',
    icon: <InstagramIcon />
  },
  {
    key: 'google',
    label: 'Google',
    description: 'Vinculá tu cuenta de Google para iniciar sesión. No se publica contenido en Google.',
    color: '#ffffff',
    icon: <GoogleIcon />
  },
  ...(FEATURE_FLAGS.ENABLE_FAKE_SOCIAL ? [{
    key: 'fake' as SocialProvider,
    label: 'FakeSocialMedia (dev)',
    description: 'Provider mock para testear OAuth, merge de cuentas y publicación sin tocar APIs reales.',
    color: '#6366f1',
    icon: <FlaskConical size={22} />
  }] : [])
];

export const ConnectModal: React.FC<ConnectModalProps> = ({ open, onClose, zIndex }) => {
  const { accounts, connectProvider } = useSocialContext();

  const isConnected = (provider: SocialProvider) =>
    accounts.some(a => a.provider === provider);

  return (
    <Modal
      open={open}
      onCancel={onClose}
      footer={null}
      width={480}
      centered
      zIndex={zIndex}
      styles={{
        content: { background: tokens.colors.primary.neutral[1000], borderRadius: 16 },
        header: { background: tokens.colors.primary.neutral[1000] }
      }}
    >
      <div style={{ padding: '8px 0 16px' }}>
        <Title level={4} style={{ margin: 0, color: tokens.colors.primary.neutral[50] }}>
          Conectar redes sociales
        </Title>
        <Text style={{ color: tokens.colors.primary.neutral[400], fontSize: 13 }}>
          Vinculá tus cuentas para publicar contenido y ganar puntos.
        </Text>

        <Divider style={{ borderColor: 'rgba(255,255,255,0.08)', margin: '20px 0' }} />

        <Space direction='vertical' size={12} style={{ width: '100%' }}>
          {PROVIDERS.map(provider => {
            const connected = isConnected(provider.key);
            return (
              <div
                key={provider.key}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 16,
                  padding: '16px',
                  borderRadius: 12,
                  background: connected
                    ? 'rgba(255, 95, 46, 0.08)'
                    : 'rgba(255, 255, 255, 0.04)',
                  border: connected
                    ? '1px solid rgba(255, 95, 46, 0.3)'
                    : '1px solid rgba(255, 255, 255, 0.08)'
                }}
              >
                {/* Provider icon */}
                <div
                  style={{
                    width: 44,
                    height: 44,
                    borderRadius: 10,
                    background: provider.color,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    color: 'white',
                    flexShrink: 0
                  }}
                >
                  {provider.icon}
                </div>

                {/* Info */}
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontWeight: 600, color: tokens.colors.primary.neutral[50], fontSize: 14 }}>
                    {provider.label}
                  </div>
                  <div style={{ color: tokens.colors.primary.neutral[400], fontSize: 12, marginTop: 2 }}>
                    {provider.description}
                  </div>
                </div>

                {/* Action */}
                {connected ? (
                  <div style={{ display: 'flex', alignItems: 'center', gap: 6, color: tokens.colors.luzu.orange, flexShrink: 0 }}>
                    <CheckCircle2 size={18} />
                    <span style={{ fontSize: 13, fontWeight: 500 }}>Conectado</span>
                  </div>
                ) : (
                  <Button
                    size='small'
                    icon={<ExternalLink size={13} />}
                    onClick={() => connectProvider(provider.key)}
                    style={{
                      flexShrink: 0,
                      background: tokens.colors.luzu.gradient,
                      border: 'none',
                      color: 'white',
                      fontWeight: 600,
                      fontSize: 13
                    }}
                  >
                    Conectar
                  </Button>
                )}
              </div>
            );
          })}
        </Space>

        <div style={{ marginTop: 20, padding: '12px 16px', borderRadius: 8, background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.06)' }}>
          <Text style={{ color: tokens.colors.primary.neutral[500], fontSize: 12 }}>
            Al conectar tu cuenta, autorizás a Doer a leer métricas públicas de tus publicaciones
            (vistas, likes, shares). Tus puntos se calculan según las vistas. No publicamos nada sin tu confirmación.
          </Text>
        </div>
      </div>
    </Modal>
  );
};
